import React, { useEffect, useState } from 'react';
import { X, CheckCircle, WarningCircle, Info } from '@phosphor-icons/react';

export function Toast({ type = 'info', title, message, onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} weight="fill" color="#10b981" />;
      case 'error':
        return <WarningCircle size={20} weight="fill" color="#ef4444" />;
      case 'warning':
        return <WarningCircle size={20} weight="fill" color="#f59e0b" />;
      default:
        return <Info size={20} weight="fill" color="#0077b6" />;
    }
  };

  return (
    <div className={`toast toast-${type} ${!isVisible ? 'toast-exit' : ''}`}>
      <div className="toast-icon">{getIcon()}</div>
      <div className="toast-content">
        {title && <div className="toast-title">{title}</div>}
        {message && <div className="toast-message">{message}</div>}
      </div>
      <button className="toast-close" onClick={onClose}>
        <X size={16} />
      </button>
    </div>
  );
}

// Toast Container Component
export function ToastContainer({ toasts, removeToast }) {
  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}

// Custom hook for managing toasts
export function useToast() {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const success = (message, title = 'Success') => addToast({ type: 'success', title, message });
  const error = (message, title = 'Error') => addToast({ type: 'error', title, message });
  const warning = (message, title = 'Warning') => addToast({ type: 'warning', title, message });
  const info = (message, title = 'Info') => addToast({ type: 'info', title, message });

  return { toasts, addToast, removeToast, success, error, warning, info };
}

export default Toast;
